import {datadogLogs} from '@datadog/browser-logs';
import * as Sentry from '@sentry/react';
import {PlanType} from '../generated/graphql';
import {Viewer} from '../state/viewer/types';
import {datadogEnabled} from './recordRender';
import {restartFullstoryScript} from './integrations';
import {getStringValueFromQueryString} from './query-string';
import wait from './wait';

if (datadogEnabled) {
  datadogLogs.init({
    clientToken: 'pubbbf74d2a526a76acb379ca4102d98808',
    forwardErrorsToLogs: false,
  });
}

interface GithubInfo {
  githubUsername?: string;
  githubCompany?: string;
  githubURL?: string;
  githubFollowers?: number;
}

function formatCompanyForAnalytics(org: Viewer['organizations'][0]) {
  let company = {
    // ID is stable and will preserve analytics history if the
    // org is ever renamed.
    id: org.id,
    name: org.name,
    website: null,
  };

  if (org.subscriptions && org.subscriptions.length > 0) {
    const sub = org.subscriptions.find(
      s => s.plan.planType === PlanType.Primary
    );
    const planTypes = org.subscriptions.map(s => s.plan.planType).join();

    company = Object.assign(company, {
      size: sub?.seats ?? 0, // .plan.maxSeats?
      plan_type: planTypes,
    });
  }

  return company;
}

async function formatViewerForAnalytics(viewer: Viewer) {
  let organization = '';
  let company = null;
  if (viewer.organizations && viewer.organizations.length > 0) {
    organization = viewer.organizations[0].name;
    company = formatCompanyForAnalytics(viewer.organizations[0]);
  }

  const metadata: any = {};
  if (viewer.teams.edges.length > 0) {
    metadata.teamCount = viewer.teams.edges.length;
    metadata.team = viewer.entity;
    // TODO: Segment / intercom doesn't seem to support arrays
    metadata.teams = viewer.teams.edges.map(e => e.node.name).join(', ');
    metadata.isPaid = viewer.teams.edges.some(e => e.node.isPaid);
  }
  const surveyAnswers = viewer.userInfo
    ? {
        howOften: viewer.userInfo.howOften,
      }
    : {};

  return {
    email: viewer.email,
    username: viewer.username,
    name: viewer.name,
    createdAt: viewer.createdAt,
    website: viewer.userInfo?.websiteUrl,
    description: viewer.userInfo?.bio,
    company,
    organization,
    ...surveyAnswers,
    ...metadata,
  };
}

/**
 * Identify with analytics platforms by viewer.
 */
export async function identifyWithViewer(viewer?: Viewer) {
  if (!viewer?.email) {
    return;
  }
  if (window.analytics) {
    const viewerForAnalytics = await formatViewerForAnalytics(viewer);
    window.analytics.identify(viewer.email, viewerForAnalytics);

    for (const edge of viewer.teams.edges) {
      const team = edge.node;
      if (team.isTeam) {
        window.analytics.group(team.name, {
          entityName: team.name,
          isPaid: team.isPaid,
        });
      }
    }
    // Fullstory is shutdown after script loads in index.html,
    // only restart Fullstory if user is logged in.
    restartFullstoryScript();
  }
  Sentry.configureScope(scope => {
    scope.setUser({
      username: viewer.username,
      entity: viewer.entity,
      email: viewer.email,
    });
  });
}

function getZendeskChatContainer(): HTMLElement | null {
  return document.getElementById('launcher')?.parentElement ?? null;
}

function getZendeskChatLauncher(): HTMLElement | null {
  return document.getElementById('zendesk-launcher');
}

async function hideOrShowElWithRetry(
  getEl: () => HTMLElement | null,
  show: boolean,
  tries = 30,
  intervalMS = 100
): Promise<void> {
  for (let i = 0; i < tries; i++) {
    const el = getEl();
    if (el != null) {
      el.style.display = show ? 'block' : 'none';
      return;
    }
    if (i >= tries - 1) {
      return;
    }
    await wait(intervalMS);
  }
}

export async function hideZendeskChat(): Promise<void> {
  await hideOrShowElWithRetry(getZendeskChatContainer, false);
  await hideOrShowElWithRetry(getZendeskChatLauncher, false);
}

export async function showZendeskChat(): Promise<void> {
  await hideOrShowElWithRetry(getZendeskChatContainer, true);
  await hideOrShowElWithRetry(getZendeskChatLauncher, true);
}

export function trackWBReferrer(): void {
  const wbReferrer = getStringValueFromQueryString('wbreferrer');
  if (wbReferrer) {
    window.analytics?.track(`User Sent By W&B Referrer`, {
      referrer: wbReferrer,
    });
  }
}
